import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { 
  getActiveFirebaseConfig, 
  saveFirebaseConfig, 
  isFirebaseConfigured 
} from '../../services/firebaseAuth';
import { 
  Building2, 
  ShieldCheck, 
  CreditCard, 
  Lock, 
  Download, 
  Upload, 
  RotateCcw, 
  Save, 
  Sparkles,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  Image as ImageIcon,
  MessageCircle,
  FileText,
  Truck,
  RefreshCw,
  Award,
  Globe,
  Share2,
  Copy,
  ExternalLink,
  Layers,
  FolderArchive,
  Tag,
  BadgePercent,
  Smartphone
} from 'lucide-react';

export default function AdminSettings() {
  const { 
    storeSettings, 
    updateStoreSettings, 
    exportStoreBackup, 
    importStoreBackup, 
    resetToDefaultData, 
    products,
    categories,
    addToast 
  } = useShop();

  const [form, setForm] = useState({ ...storeSettings });
  const [activeTab, setActiveTab] = useState('brand');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [firebaseConfigState, setFirebaseConfigState] = useState(getActiveFirebaseConfig());
  const [firebaseRawSnippet, setFirebaseRawSnippet] = useState('');
  const [firebaseStatus, setFirebaseStatus] = useState(isFirebaseConfigured());

  const handleSaveFirebaseConfig = (e) => {
    e?.preventDefault();
    const success = saveFirebaseConfig(firebaseConfigState);
    if (success) {
      setFirebaseStatus(isFirebaseConfigured());
      addToast('Firebase credentials saved successfully! Phone OTP login is active.', 'success');
    } else {
      addToast('Failed to save Firebase credentials.', 'error');
    }
  };

  const handleParseRawSnippet = () => {
    if (!firebaseRawSnippet.trim()) {
      addToast('Please paste your Firebase snippet first.', 'error');
      return;
    }
    try {
      const text = firebaseRawSnippet;
      const extract = (key) => {
        const regex = new RegExp(`['"]?${key}['"]?\\s*:\\s*['"]([^'"]+)['"]`, 'i');
        const match = text.match(regex);
        return match ? match[1] : '';
      };

      const parsedConfig = {
        apiKey: extract('apiKey') || firebaseConfigState.apiKey,
        authDomain: extract('authDomain') || firebaseConfigState.authDomain,
        projectId: extract('projectId') || firebaseConfigState.projectId,
        storageBucket: extract('storageBucket') || firebaseConfigState.storageBucket,
        messagingSenderId: extract('messagingSenderId') || firebaseConfigState.messagingSenderId,
        appId: extract('appId') || firebaseConfigState.appId,
      };

      if (!parsedConfig.apiKey || !parsedConfig.projectId) {
        addToast('Could not automatically parse apiKey and projectId. Please paste values manually.', 'error');
        return;
      }

      setFirebaseConfigState(parsedConfig);
      saveFirebaseConfig(parsedConfig);
      setFirebaseStatus(isFirebaseConfigured());
      addToast('Firebase configuration automatically parsed and saved!', 'success');
      setFirebaseRawSnippet('');
    } catch (err) {
      addToast('Failed to parse snippet: ' + err.message, 'error');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateStoreSettings(form);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      addToast('Logo image must be under 5MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm((prev) => ({ ...prev, logoUrl: event.target?.result }));
      addToast('Logo image loaded! Click "Save Settings" to publish.', 'info');
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result);
        const success = importStoreBackup(json);
        if (success) {
          setForm(json.storeSettings || storeSettings);
        }
      } catch (err) {
        addToast('Invalid JSON file format. Could not restore backup.', 'error');
      }
    };
    reader.readAsText(file);
  };

  const handleExportMultiStoreTemplate = () => {
    exportStoreBackup();
    addToast('Multi-Store Template JSON exported! You can import this into any new jewellery website.', 'success');
  };

  const tabs = [
    { id: 'brand', label: 'Visual Brand & Logo', icon: <ImageIcon size={16} /> },
    { id: 'offers', label: 'Product Offers & Delivery Badges', icon: <Tag size={16} /> },
    { id: 'contact', label: 'Showroom & Contact Details', icon: <Phone size={16} /> },
    { id: 'footerCustom', label: 'Registered Office & App Download', icon: <Building2 size={16} /> },
    { id: 'payment', label: 'Razorpay Gateway', icon: <CreditCard size={16} /> },
    { id: 'firebase', label: 'Firebase Phone OTP Login', icon: <Smartphone size={16} /> },
    { id: 'policies', label: 'Policies & Legal Compliance', icon: <FileText size={16} /> },
    { id: 'shipping', label: 'Courier & Delivery Charges', icon: <Truck size={16} /> },
    { id: 'backup', label: 'Multi-Store Backup & Clone Hub', icon: <FolderArchive size={16} /> },
  ];

  return (
    <div style={{ maxWidth: '1050px' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.8rem' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
          <Sparkles size={13} /> White-Label Brand & Multi-Store Management Suite
        </div>
        <h2 style={{ fontSize: '1.65rem', fontWeight: 800, color: '#fff', margin: 0 }}>
          Store Branding & <span className="text-gold-gradient">System Configuration</span>
        </h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
          Fully customize brand identity, logos, showroom contacts, legal policies, and export turnkey multi-store backups.
        </p>
      </div>

      {/* Tabs Navigation */}
      <div style={{
        display: 'flex',
        overflowX: 'auto',
        gap: '0.5rem',
        marginBottom: '1.8rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        paddingBottom: '0.75rem'
      }} className="hide-scrollbar">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1.1rem',
                borderRadius: 'var(--radius-sm)',
                border: isActive ? '1px solid var(--gold-400)' : '1px solid var(--border-subtle)',
                background: isActive ? 'var(--gold-gradient)' : 'rgba(255, 255, 255, 0.03)',
                color: isActive ? '#0a0a0f' : 'var(--text-secondary)',
                fontWeight: isActive ? 800 : 600,
                fontSize: '0.84rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'var(--transition)'
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        
        {/* ========================================================================= */}
        {/* TAB 1: VISUAL BRAND & LOGO */}
        {/* ========================================================================= */}
        {activeTab === 'brand' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Logo Upload & Customization Card */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <ImageIcon size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Brand Logo & Favicon
                </h3>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1.3rem' }}>
                Upload your official boutique logo. When set, this logo replaces the default emblem across the Navbar header, footer, and tax invoice headers.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '1.5rem', alignItems: 'flex-start' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Logo Image URL or Local File
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem' }}>
                    <input
                      type="text"
                      placeholder="https://yourdomain.com/logo.png or browse local file"
                      value={form.logoUrl || ''}
                      onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                      className="input-luxury"
                      style={{ flex: 1 }}
                    />
                    <label
                      className="btn btn-gold btn-sm"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Upload size={14} /> Browse Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>

                  {form.logoUrl && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, logoUrl: '' })}
                      className="btn-glass btn-sm"
                      style={{ fontSize: '0.74rem', color: '#f87171' }}
                    >
                      Remove Custom Logo (Revert to Gold Typography)
                    </button>
                  )}
                </div>

                {/* Live Logo Preview Box */}
                <div style={{
                  background: '#0a0a0f',
                  border: '1px dashed var(--border-gold)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1.2rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '120px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.6rem', textTransform: 'uppercase', fontWeight: 700 }}>
                    Header Logo Live Preview
                  </div>
                  {form.logoUrl ? (
                    <img
                      src={form.logoUrl}
                      alt="Brand Logo Preview"
                      style={{ maxHeight: '52px', maxWidth: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        addToast('Failed to render logo preview. Please verify URL.', 'error');
                      }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--gold-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Sparkles size={18} color="#0a0a0f" />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>{form.storeName || 'Brand Name'}</div>
                        <div style={{ fontSize: '0.65rem', color: 'var(--gold-400)', textTransform: 'uppercase' }}>{form.tagline || 'Jewellery Tagline'}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Brand Entity Name & Story */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <Building2 size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Showroom Entity & Heritage Story
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginBottom: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Store / Enterprise Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Royal Fine Jewels & Gold"
                    value={form.storeName || ''}
                    onChange={(e) => setForm({ ...form, storeName: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Brand Tagline / Slogan
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Pure BIS 916 Hallmarked Gold & Solitaires"
                    value={form.tagline || ''}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Brand Heritage Legacy Narrative (Displays in Footer & About Modal)
                </label>
                <textarea
                  rows={4}
                  placeholder="Describe your jewellery house legacy, craftsmanship heritage, hallmarking commitment, and years of trust..."
                  value={form.aboutStory || ''}
                  onChange={(e) => setForm({ ...form, aboutStory: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB: PRODUCT OFFERS & DELIVERY BADGES (CUSTOMIZE SCREENSHOT HIGHLIGHTS) */}
        {/* ========================================================================= */}
        {activeTab === 'offers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Header info card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, #13131e 100%)',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.6rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
                <Tag size={22} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Product Page "Available Offers" & Delivery Subtitle Customization
                </h3>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                Yahan se aap product page par dikhne wale <strong>Available Offers</strong> (Bank Offer, Making Discount, etc.) aur <strong>Delivery Notice</strong> ko customize kar sakte hain. Cash on Delivery (COD) ko jewellery safety ke liye permanently remove kar diya gaya hai.
              </p>
            </div>

            {/* 1. AVAILABLE OFFERS CUSTOMIZATION */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem', borderBottom: '1px solid rgba(255, 255, 255, 0.06)', paddingBottom: '0.8rem' }}>
                <div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                    1. Product Page "Available Offers" List
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>
                    Ye offers har product detail page par live display hote hain
                  </p>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, color: form.productOffersEnabled !== false ? '#34d399' : 'var(--text-muted)' }}>
                  <input
                    type="checkbox"
                    checked={form.productOffersEnabled !== false}
                    onChange={(e) => setForm({ ...form, productOffersEnabled: e.target.checked })}
                    style={{ accentColor: 'var(--gold-400)', width: '16px', height: '16px' }}
                  />
                  {form.productOffersEnabled !== false ? '✓ Offers Enabled' : '✕ Offers Disabled'}
                </label>
              </div>

              {/* Offer 1 */}
              <div style={{ background: '#0d0d16', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#10b981', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🏷️ Offer #1
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.8rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Title</label>
                    <input
                      type="text"
                      value={form.productOffer1Title ?? 'Bank Offer'}
                      onChange={(e) => setForm({ ...form, productOffer1Title: e.target.value })}
                      className="input-luxury"
                      placeholder="Bank Offer"
                      style={{ fontWeight: 700 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Offer Description / Terms</label>
                    <input
                      type="text"
                      value={form.productOffer1Text ?? '10% Instant Discount up to ₹1,500 on HDFC / ICICI Credit Cards'}
                      onChange={(e) => setForm({ ...form, productOffer1Text: e.target.value })}
                      className="input-luxury"
                      placeholder="10% Instant Discount up to ₹1,500 on HDFC / ICICI Credit Cards"
                    />
                  </div>
                </div>
              </div>

              {/* Offer 2 */}
              <div style={{ background: '#0d0d16', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#10b981', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🏷️ Offer #2
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.8rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Title</label>
                    <input
                      type="text"
                      value={form.productOffer2Title ?? 'Special Price'}
                      onChange={(e) => setForm({ ...form, productOffer2Title: e.target.value })}
                      className="input-luxury"
                      placeholder="Special Price"
                      style={{ fontWeight: 700 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Offer Description / Terms</label>
                    <input
                      type="text"
                      value={form.productOffer2Text ?? 'Get direct artisan pricing with zero middleman wastage'}
                      onChange={(e) => setForm({ ...form, productOffer2Text: e.target.value })}
                      className="input-luxury"
                      placeholder="Get direct artisan pricing with zero middleman wastage"
                    />
                  </div>
                </div>
              </div>

              {/* Offer 3 */}
              <div style={{ background: '#0d0d16', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#10b981', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🏷️ Offer #3
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.8rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Title</label>
                    <input
                      type="text"
                      value={form.productOffer3Title ?? 'Special Offer'}
                      onChange={(e) => setForm({ ...form, productOffer3Title: e.target.value })}
                      className="input-luxury"
                      placeholder="Special Offer"
                      style={{ fontWeight: 700 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Offer Description / Terms</label>
                    <input
                      type="text"
                      value={form.productOffer3Text ?? "Flat 50% OFF on Making Charges on today's orders"}
                      onChange={(e) => setForm({ ...form, productOffer3Text: e.target.value })}
                      className="input-luxury"
                      placeholder="Flat 50% OFF on Making Charges on today's orders"
                    />
                  </div>
                </div>
              </div>

              {/* Offer 4 (Optional) */}
              <div style={{ background: '#0d0d16', padding: '1rem', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 800, color: 'var(--gold-400)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  🏷️ Offer #4 (Optional)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.8rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Title</label>
                    <input
                      type="text"
                      value={form.productOffer4Title ?? 'Gold Guarantee'}
                      onChange={(e) => setForm({ ...form, productOffer4Title: e.target.value })}
                      className="input-luxury"
                      placeholder="Gold Guarantee"
                      style={{ fontWeight: 700 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>Offer Description / Terms</label>
                    <input
                      type="text"
                      value={form.productOffer4Text ?? '100% Lifetime Gold & Silver Exchange Guarantee'}
                      onChange={(e) => setForm({ ...form, productOffer4Text: e.target.value })}
                      className="input-luxury"
                      placeholder="100% Lifetime Gold & Silver Exchange Guarantee"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 2. DELIVERY SUBTITLE & COD STATUS */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                <Truck size={20} color="var(--gold-400)" />
                <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  2. Delivery Subtitle & Pincode Notice (No COD)
                </h4>
              </div>
              
              <div style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: '6px',
                padding: '0.8rem 1rem',
                marginBottom: '1.2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem'
              }}>
                <span style={{ fontSize: '1.1rem' }}>🛡️</span>
                <span style={{ fontSize: '0.82rem', color: '#fca5a5' }}>
                  <strong>Cash on Delivery (COD) Removed:</strong> Aapke anurodh par product page se "Cash on Delivery" text ko poori tarah hata diya gaya hai taaki gold jewellery orders prepaid & secure rahen.
                </span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Delivery Subtitle (Pincode Check ke neeche dikhne wala text)
                </label>
                <input
                  type="text"
                  value={form.deliverySubtitle ?? '✓ 100% Insured Armored Transit • Pre-Paid Verified Dispatch'}
                  onChange={(e) => setForm({ ...form, deliverySubtitle: e.target.value })}
                  className="input-luxury"
                  placeholder="✓ 100% Insured Armored Transit • Pre-Paid Verified Dispatch"
                  style={{ fontWeight: 600 }}
                />
              </div>

              {/* Quick Preset Subtitles */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.7rem' }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', alignSelf: 'center' }}>Presets:</span>
                {[
                  '✓ 100% Insured Armored Transit • Pre-Paid Verified Dispatch',
                  '✓ 100% Insured Transit • Safe Vault Dispatch • UPI Available',
                  '✓ BIS Hallmarked 916/750 • Insured Armored Transit',
                  '✓ Tamper-Evident Packaging • Pre-Paid Priority Shipping'
                ].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setForm({ ...form, deliverySubtitle: preset })}
                    style={{
                      fontSize: '0.7rem',
                      padding: '3px 8px',
                      borderRadius: '4px',
                      background: form.deliverySubtitle === preset ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      color: form.deliverySubtitle === preset ? 'var(--gold-300)' : 'var(--text-muted)',
                      border: form.deliverySubtitle === preset ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.1)',
                      cursor: 'pointer'
                    }}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>

            {/* 3. LIVE VISUAL PREVIEW OF PRODUCT PAGE SECTION */}
            <div style={{
              background: '#ffffff',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem',
              color: '#1f2937',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '2px solid var(--gold-400)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.6rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', color: '#b45309', letterSpacing: '0.05em' }}>
                  👁️ Live Customer View (Product Detail Page Preview)
                </span>
                <span style={{ fontSize: '0.7rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>
                  Instant Real-Time Preview
                </span>
              </div>

              {/* Available offers Preview Box */}
              {form.productOffersEnabled !== false && (
                <div style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.2rem',
                  marginBottom: '1rem',
                  background: '#fafafa'
                }}>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', marginBottom: '0.8rem' }}>
                    Available offers
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {Boolean(form.productOffer1Title || form.productOffer1Text) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: '#374151' }}>
                        <Tag size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div>
                          <strong>{form.productOffer1Title ?? 'Bank Offer'}:</strong> {form.productOffer1Text ?? '10% Instant Discount up to ₹1,500 on HDFC / ICICI Credit Cards'}
                        </div>
                      </div>
                    )}
                    {Boolean(form.productOffer2Title || form.productOffer2Text) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: '#374151' }}>
                        <Tag size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div>
                          <strong>{form.productOffer2Title ?? 'Special Price'}:</strong> {form.productOffer2Text ?? 'Get direct artisan pricing with zero middleman wastage'}
                        </div>
                      </div>
                    )}
                    {Boolean(form.productOffer3Title || form.productOffer3Text) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: '#374151' }}>
                        <Tag size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div>
                          <strong>{form.productOffer3Title ?? 'Special Offer'}:</strong> {form.productOffer3Text ?? "Flat 50% OFF on Making Charges on today's orders"}
                        </div>
                      </div>
                    )}
                    {Boolean(form.productOffer4Title && form.productOffer4Text) && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: '#374151' }}>
                        <Tag size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div>
                          <strong>{form.productOffer4Title}:</strong> {form.productOffer4Text}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Delivery Preview Box */}
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1.2rem',
                background: '#ffffff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', fontWeight: 600, color: '#374151' }}>
                    <MapPin size={16} color="#6b7280" /> Delivery
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', borderBottom: '2px solid #2563eb', padding: '2px 8px' }}>
                      302001
                    </span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2563eb' }}>Check</span>
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827', marginBottom: '0.3rem' }}>
                  Delivery by 3-5 Days | <span style={{ color: '#16a34a' }}>FREE</span>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 500 }}>
                  {form.deliverySubtitle || '✓ 100% Insured Armored Transit • Pre-Paid Verified Dispatch'}
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: SHOWROOM & CONTACT DETAILS */}
        {/* ========================================================================= */}
        {activeTab === 'contact' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <Phone size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Showroom Contacts & VIP WhatsApp Concierge
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem', marginBottom: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Primary Showroom Phone *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="+91 98290 12345"
                    value={form.phone || ''}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    VIP WhatsApp Concierge Number *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="+91 98290 12345"
                    value={form.whatsapp || ''}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    className="input-luxury"
                  />
                  <div style={{ fontSize: '0.7rem', color: '#34d399', marginTop: '0.3rem' }}>
                    ● Used for direct 1-click customer WhatsApp inquiries across product pages & footer
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Support / Inquiries Email *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="concierge@jewellery.com"
                    value={form.email || ''}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.2rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Full Flagship Boutique Physical Address *
                </label>
                <textarea
                  rows={2}
                  required
                  placeholder="Showroom Name, Street, Market, City, State - PIN Code"
                  value={form.address || ''}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', marginBottom: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Showroom Timings / Visiting Hours *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Mon - Sun: 10:30 AM to 08:30 PM (All 7 Days Open)"
                    value={form.storeTimings || ''}
                    onChange={(e) => setForm({ ...form, storeTimings: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Google Maps Showroom Location URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://maps.google.com/?q=..."
                    value={form.googleMapsUrl || ''}
                    onChange={(e) => setForm({ ...form, googleMapsUrl: e.target.value, storeGoogleMapsUrl: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>
            </div>

            {/* Social Handles */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <Share2 size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Social Media & Boutique Channels
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Instagram Profile Link
                  </label>
                  <input
                    type="url"
                    placeholder="https://instagram.com/your_boutique"
                    value={form.socialLinks?.instagram || ''}
                    onChange={(e) => setForm({
                      ...form,
                      socialLinks: { ...form.socialLinks, instagram: e.target.value }
                    })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Facebook Page Link
                  </label>
                  <input
                    type="url"
                    placeholder="https://facebook.com/your_boutique"
                    value={form.socialLinks?.facebook || ''}
                    onChange={(e) => setForm({
                      ...form,
                      socialLinks: { ...form.socialLinks, facebook: e.target.value }
                    })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    YouTube Channel Link
                  </label>
                  <input
                    type="url"
                    placeholder="https://youtube.com/@your_boutique"
                    value={form.socialLinks?.youtube || ''}
                    onChange={(e) => setForm({
                      ...form,
                      socialLinks: { ...form.socialLinks, youtube: e.target.value }
                    })}
                    className="input-luxury"
                  />
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB: REGISTERED OFFICE & APP DOWNLOAD CUSTOMIZATION */}
        {/* ========================================================================= */}
        {activeTab === 'footerCustom' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Header info card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, #13131e 100%)',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.6rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
                <Building2 size={22} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Footer Registered Office & App Download Customization
                </h3>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
                Website ke footer me dikhne wale <strong>Registered Office legal details</strong> aur <strong>Android App Download Banner</strong> ko yahan se show/hide karein ya apni company ke hisab se customize karein.
              </p>
            </div>

            {/* 1. REGISTERED OFFICE DETAILS */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.4rem', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                    🏢 Registered Office Legal Details
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                    Footer se is block ko hatane ke liye toggle OFF rakhein ("Hata diya gaya"). Dikhane ke liye ON karein.
                  </p>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '4px' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.showRegisteredOffice)}
                    onChange={(e) => setForm({ ...form, showRegisteredOffice: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--gold-400)' }}
                  />
                  <span style={{ fontSize: '0.84rem', fontWeight: 800, color: form.showRegisteredOffice ? 'var(--gold-400)' : '#94a3b8' }}>
                    {form.showRegisteredOffice ? 'VISIBLE (ENABLED)' : 'HIDDEN (DISABLED)'}
                  </span>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem', opacity: form.showRegisteredOffice ? 1 : 0.6 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Registered Legal Entity / Company Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. M.L. Jewellers & Kitchenware Private Limited"
                    value={form.registeredCompanyName || ''}
                    onChange={(e) => setForm({ ...form, registeredCompanyName: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Corporate CIN Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. U36911RJ2026PTC099182"
                    value={form.cinNumber || ''}
                    onChange={(e) => setForm({ ...form, cinNumber: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    BIS Hallmark License Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. HM-916-2026-IND"
                    value={form.registeredOfficeBisLicense || ''}
                    onChange={(e) => setForm({ ...form, registeredOfficeBisLicense: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    GSTIN Registration Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 08AAECM9182P1ZV"
                    value={form.registeredOfficeGstin || ''}
                    onChange={(e) => setForm({ ...form, registeredOfficeGstin: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>

              <div style={{ marginTop: '1.2rem', opacity: form.showRegisteredOffice ? 1 : 0.6 }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Registered Office Address (Optional Override)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Sangh Bhawan, Fazalganj, Sasaram Near By Durgachokh 821115"
                  value={form.registeredOfficeAddress || ''}
                  onChange={(e) => setForm({ ...form, registeredOfficeAddress: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            </div>

            {/* 2. ANDROID APP DOWNLOAD BANNER IN FOOTER */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.4rem', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '1rem', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                    📱 Android App Download Card in Footer
                  </h4>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                    Footer me se yellow download button card hatane ke liye toggle OFF rakhein ("Iska hata dijie").
                  </p>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '4px' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.showAppDownloadBanner)}
                    onChange={(e) => setForm({ ...form, showAppDownloadBanner: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--gold-400)' }}
                  />
                  <span style={{ fontSize: '0.84rem', fontWeight: 800, color: form.showAppDownloadBanner ? 'var(--gold-400)' : '#94a3b8' }}>
                    {form.showAppDownloadBanner ? 'VISIBLE (ENABLED)' : 'HIDDEN (DISABLED)'}
                  </span>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem', opacity: form.showAppDownloadBanner ? 1 : 0.6 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    App Title
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. M.L. Jewellers Android App"
                    value={form.appDownloadTitle || ''}
                    onChange={(e) => setForm({ ...form, appDownloadTitle: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Download Button Text
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. DOWNLOAD APK (24 MB)"
                    value={form.appDownloadButtonText || ''}
                    onChange={(e) => setForm({ ...form, appDownloadButtonText: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    App Subtitle / Description
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Instant live gold rates, hallmark scanner & zero-wastage calculator on your phone."
                    value={form.appDownloadSubtitle || ''}
                    onChange={(e) => setForm({ ...form, appDownloadSubtitle: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    APK Download URL / File Link
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. /mljewellers-android.apk"
                    value={form.appDownloadUrl || ''}
                    onChange={(e) => setForm({ ...form, appDownloadUrl: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-gold"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 2rem', fontSize: '0.95rem', fontWeight: 800 }}
              >
                <Save size={16} /> Save Footer & App Settings
              </button>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: POLICIES & COMPLIANCE */}
        {/* ========================================================================= */}
        {activeTab === 'policies' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Regulatory & Tax Compliance */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <ShieldCheck size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Statutory BIS Hallmarking & GST Compliance
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem', marginBottom: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Official GSTIN *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="08AAAFM1234F1Z8"
                    value={form.gstin || ''}
                    onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    BIS Hallmark Certificate License No.
                  </label>
                  <input
                    type="text"
                    placeholder="HM/C-7689123490"
                    value={form.bisLicense || ''}
                    onChange={(e) => setForm({ ...form, bisLicense: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Official UPI ID for Direct Checkout
                  </label>
                  <input
                    type="text"
                    placeholder="boutique@icici"
                    value={form.upiId || ''}
                    onChange={(e) => setForm({ ...form, upiId: e.target.value })}
                    className="input-luxury"
                  />
                </div>
              </div>
            </div>

            {/* Legal Policies Rich Text Editors */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <FileText size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Store Legal Policies & Consumer Assurances
                </h3>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1.3rem' }}>
                These policies display immediately to your patrons in the footer and during checkout. Customize each to match your store's terms.
              </p>

              {/* 1. Privacy Policy */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Privacy & Data Security Policy
                </label>
                <textarea
                  rows={5}
                  value={form.privacyPolicy || ''}
                  onChange={(e) => setForm({ ...form, privacyPolicy: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.82rem', lineHeight: '1.5' }}
                />
              </div>

              {/* 2. Terms & Conditions */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Terms of Sale & Bullion Rate Conditions
                </label>
                <textarea
                  rows={5}
                  value={form.termsConditions || ''}
                  onChange={(e) => setForm({ ...form, termsConditions: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.82rem', lineHeight: '1.5' }}
                />
              </div>

              {/* 3. Return & Exchange Policy */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  15-Day Return & Lifetime 100% Exchange Guarantee
                </label>
                <textarea
                  rows={5}
                  value={form.returnPolicy || ''}
                  onChange={(e) => setForm({ ...form, returnPolicy: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.82rem', lineHeight: '1.5' }}
                />
              </div>

              {/* 4. Insured Armored Shipping Policy */}
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                  Insured Armored Transit & Delivery Policy
                </label>
                <textarea
                  rows={5}
                  value={form.shippingPolicy || ''}
                  onChange={(e) => setForm({ ...form, shippingPolicy: e.target.value })}
                  className="input-luxury"
                  style={{ width: '100%', resize: 'vertical', fontSize: '0.82rem', lineHeight: '1.5' }}
                />
              </div>

            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: COURIER & DELIVERY CHARGES & FULFILLMENT PLAN */}
        {/* ========================================================================= */}
        {activeTab === 'shipping' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Global Dispatch & Delivery Plan */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                <Truck size={22} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Storewide Default Availability & Delivery Timelines
                </h3>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1.4rem' }}>
                These are the fallback timelines shown on product pages and quick-view modals across your catalog whenever an individual product does not have a custom timeline set.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.4rem' }}>
                {/* Default Availability */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#34d399', marginBottom: '0.4rem' }}>
                    ● Default Availability Status (Dispatch Timeline)
                  </label>
                  <input
                    type="text"
                    value={form.defaultAvailability || ''}
                    onChange={(e) => setForm({ ...form, defaultAvailability: e.target.value })}
                    className="input-luxury"
                    placeholder="e.g. In Stock (Dispatch in 24-48 Hours)"
                    style={{ fontWeight: 600, color: '#34d399' }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {[
                      'In Stock (Dispatch in 24-48 Hours)',
                      'Made to Order (7-10 Days)',
                      'Ready for Showroom Pickup',
                      'Handcrafted on Demand (12-15 Days)'
                    ].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setForm({ ...form, defaultAvailability: preset })}
                        style={{
                          fontSize: '0.68rem',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          background: form.defaultAvailability === preset ? 'rgba(52, 211, 153, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                          color: form.defaultAvailability === preset ? '#34d399' : 'var(--text-muted)',
                          border: form.defaultAvailability === preset ? '1px solid #34d399' : '1px solid rgba(255, 255, 255, 0.1)',
                          cursor: 'pointer'
                        }}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Default Delivery */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: 'var(--gold-300)', marginBottom: '0.4rem' }}>
                    🚚 Default Delivery Timeline (Transit Duration)
                  </label>
                  <input
                    type="text"
                    value={form.defaultDelivery || ''}
                    onChange={(e) => setForm({ ...form, defaultDelivery: e.target.value })}
                    className="input-luxury"
                    placeholder="e.g. 3-5 Days (Insured Nationwide Transit)"
                    style={{ fontWeight: 600, color: '#fff' }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
                    {[
                      '3-5 Days (Insured Nationwide Transit)',
                      '2-4 Days Express Air Courier',
                      'Same Day Local Courier',
                      '5-7 Days Standard Insured Transit'
                    ].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setForm({ ...form, defaultDelivery: preset })}
                        style={{
                          fontSize: '0.68rem',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          background: form.defaultDelivery === preset ? 'rgba(212, 175, 55, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                          color: form.defaultDelivery === preset ? 'var(--gold-300)' : 'var(--text-muted)',
                          border: form.defaultDelivery === preset ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.1)',
                          cursor: 'pointer'
                        }}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Courier Partner & Shipping Charges */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', marginBottom: '1.2rem' }}>
                Logistics Partner & Shipping Rates
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Armored Courier / Logistics Partner Name
                  </label>
                  <input
                    type="text"
                    placeholder="BVC Logistics Armored Vault Express"
                    value={form.courierName || ''}
                    onChange={(e) => setForm({ ...form, courierName: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Flat Shipping Charge (₹, 0 = Free Delivery)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={form.courierCharge ?? 0}
                    onChange={(e) => setForm({ ...form, courierCharge: parseFloat(e.target.value) || 0 })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Free Shipping Minimum Order Value (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="50000"
                    value={form.freeShippingThreshold ?? 50000}
                    onChange={(e) => setForm({ ...form, freeShippingThreshold: parseFloat(e.target.value) || 0 })}
                    className="input-luxury"
                  />
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: MULTI-STORE BACKUP & CLONING HUB */}
        {/* ========================================================================= */}
        {activeTab === 'backup' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.09) 0%, #13131e 100%)',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
                <FolderArchive size={22} color="var(--gold-300)" />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Turnkey Multi-Store Deployment & Backup Kit
                </h3>
              </div>
              <p style={{ fontSize: '0.86rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1.4rem' }}>
                This allows you to take a complete snapshot of this jewellery e-commerce platform and use it to deploy fresh jewellery websites for any client. The export includes all products, master categories, bullion rates, visual CMS configurations, and white-label policy templates.
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                {/* Export Card */}
                <div style={{
                  background: '#0d0d16',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1.3rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gold-400)', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>
                    <Download size={16} /> 1-Click Multi-Store JSON Export
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                    Downloads complete JSON database package with {products.length} products, {categories.length} categories, bullion rates, and policies.
                  </p>
                  <button
                    type="button"
                    onClick={handleExportMultiStoreTemplate}
                    className="btn btn-gold btn-sm"
                    style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
                  >
                    <Download size={14} /> Download Full Store Backup
                  </button>
                </div>

                {/* Import Card */}
                <div style={{
                  background: '#0d0d16',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1.3rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#34d399', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.4rem' }}>
                    <Upload size={16} /> Import / Restore Existing Store Backup
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                    Restore any previously exported JSON database package to instantly populate this site with products and settings.
                  </p>
                  <label
                    className="btn btn-glass btn-sm"
                    style={{
                      width: '100%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.4rem',
                      cursor: 'pointer',
                      borderColor: 'rgba(52, 211, 153, 0.3)',
                      color: '#34d399'
                    }}
                  >
                    <Upload size={14} /> Select Backup File (.json)
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>

              {/* Standalone Package Notice */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(212, 175, 55, 0.25)',
                borderRadius: 'var(--radius-sm)',
                padding: '1rem 1.2rem',
                fontSize: '0.8rem',
                color: 'var(--text-secondary)'
              }}>
                <div style={{ fontWeight: 700, color: 'var(--gold-300)', marginBottom: '0.3rem' }}>
                  📂 Standalone Files Located in Codebase:
                </div>
                <div>
                  We have also packaged a dedicated folder <code>multi_store_package/</code> containing:
                  <ul style={{ margin: '0.4rem 0 0 1.2rem', lineHeight: 1.6 }}>
                    <li><code>full_store_catalog_backup.json</code> — Standalone database snapshot</li>
                    <li><code>seed_new_store.js</code> — 1-Command database hydration script for new clients</li>
                    <li><code>README_CLONE_GUIDE.md</code> — Step-by-step cloning and deployment guide</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Admin PIN & Security */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.2rem' }}>
                <Lock size={20} color="var(--gold-400)" />
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Admin Security Credentials
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Master Admin Email
                  </label>
                  <input
                    type="email"
                    value={form.adminEmail || ''}
                    onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Admin Password
                  </label>
                  <input
                    type="password"
                    value={form.adminPassword || ''}
                    placeholder="Enter new password to change"
                    onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.4rem' }}>
                    Security Unlock PIN (4-6 Digits)
                  </label>
                  <input
                    type="password"
                    maxLength={6}
                    value={form.adminPin || ''}
                    placeholder="••••"
                    onChange={(e) => setForm({ ...form, adminPin: e.target.value })}
                    className="input-luxury"
                    style={{ letterSpacing: '0.3em', fontSize: '1rem', fontWeight: 700 }}
                  />
                </div>
              </div>
            </div>

            {/* Factory Reset Safety Box */}
            <div style={{
              background: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: 'var(--radius-md)',
              padding: '1.4rem 1.8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              <div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#f87171' }}>
                  Reset Entire Platform to Factory Defaults
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Restores initial catalog, gold rates, and royal fine gold settings.
                </div>
              </div>

              <button
                type="button"
                onClick={() => setResetConfirmOpen(true)}
                className="btn btn-sm"
                style={{
                  background: 'transparent',
                  border: '1px solid #ef4444',
                  color: '#ef4444',
                  fontSize: '0.8rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                <RotateCcw size={14} /> Factory Reset
              </button>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB: RAZORPAY PAYMENT GATEWAY CONFIGURATION */}
        {/* ========================================================================= */}
        {activeTab === 'payment' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Status & Banner Card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, #13131e 100%)',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #0c2340 0%, #001226 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CreditCard size={22} color="var(--gold-400)" />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                      Razorpay Online Payment Gateway
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                      Accept instant payments via UPI (GPay, PhonePe, Paytm), Debit/Credit Cards, Net Banking & Wallets.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{
                    background: (form.razorpayKeyId && form.razorpayKeyId.startsWith('rzp_test_')) ? 'rgba(234, 179, 8, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                    border: (form.razorpayKeyId && form.razorpayKeyId.startsWith('rzp_test_')) ? '1px solid #eab308' : '1px solid #10b981',
                    color: (form.razorpayKeyId && form.razorpayKeyId.startsWith('rzp_test_')) ? '#facc15' : '#34d399',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {(form.razorpayKeyId && form.razorpayKeyId.startsWith('rzp_test_')) ? '⚡ Test Mode Active' : '● Live Production'}
                  </span>

                  <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    background: form.razorpayEnabled !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    border: form.razorpayEnabled !== false ? '1px solid #10b981' : '1px solid #ef4444',
                    padding: '0.35rem 0.8rem',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: form.razorpayEnabled !== false ? '#34d399' : '#f87171'
                  }}>
                    <input
                      type="checkbox"
                      checked={form.razorpayEnabled !== false}
                      onChange={(e) => setForm(prev => ({ ...prev, razorpayEnabled: e.target.checked }))}
                      style={{ accentColor: '#10b981' }}
                    />
                    {form.razorpayEnabled !== false ? 'Gateway Enabled' : 'Gateway Disabled'}
                  </label>
                </div>
              </div>

              <div style={{
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                fontSize: '0.8rem',
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
                borderLeft: '3px solid var(--gold-400)'
              }}>
                💡 Patrons can pay directly using UPI (PhonePe, GPay, Paytm, BHIM), all RuPay, Visa & Mastercard debit/credit cards, and 50+ Indian banks. Payment confirmations update the order status in real-time.
              </div>
            </div>

            {/* API Credentials Card */}
            <div style={{
              background: '#13131e',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Lock size={16} color="var(--gold-400)" /> Merchant API Keys
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.2rem' }}>
                {/* Key ID */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
                    Razorpay Key ID *
                  </label>
                  <input
                    type="text"
                    value={form.razorpayKeyId || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, razorpayKeyId: e.target.value.trim() }))}
                    placeholder="rzp_test_... or rzp_live_..."
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: '#0d0d16',
                      border: '1px solid rgba(212, 175, 55, 0.35)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem',
                      fontFamily: 'monospace'
                    }}
                  />
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    Publishable Key used on checkout modal (e.g. <code style={{ color: 'var(--gold-300)' }}>rzp_test_TkcMWJI1AFGNfu</code>)
                  </span>
                </div>

                {/* Key Secret */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
                    Razorpay Key Secret *
                  </label>
                  <input
                    type="password"
                    value={form.razorpayKeySecret || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, razorpayKeySecret: e.target.value.trim() }))}
                    placeholder="Enter Key Secret"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: '#0d0d16',
                      border: '1px solid rgba(212, 175, 55, 0.35)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem',
                      fontFamily: 'monospace'
                    }}
                  />
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    Private Secret key used for secure webhook and signature HMAC verification
                  </span>
                </div>
              </div>

              {/* Supported Payment Channels */}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.2rem', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff', marginBottom: '0.6rem' }}>
                  Supported Payment Channels (Configured automatically):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {[
                    '✓ UPI (PhonePe, GPay, Paytm, BHIM)',
                    '✓ Credit Cards (Visa, Mastercard, RuPay, Amex)',
                    '✓ Debit Cards (ATM Cards with PIN/OTP)',
                    '✓ Net Banking (50+ Banks)',
                    '✓ Wallets (Paytm, Mobikwik, etc.)',
                    '✓ No Cost / Standard EMI Options'
                  ].map((ch, i) => (
                    <span key={i} style={{
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: 'var(--gold-300)',
                      fontSize: '0.75rem',
                      padding: '0.3rem 0.65rem',
                      borderRadius: '6px'
                    }}>
                      {ch}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Showroom UPI QR Code Configuration Card */}
            <div style={{
              background: '#13131e',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <QrCode size={18} color="var(--gold-400)" /> Showroom UPI QR Code Gateway Settings
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.2rem' }}>
                {/* Official UPI ID */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
                    Official Shop UPI ID *
                  </label>
                  <input
                    type="text"
                    value={form.upiId || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, upiId: e.target.value.trim() }))}
                    placeholder="e.g. mljewellers@icici or 9829012345@upi"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: '#0d0d16',
                      border: '1px solid rgba(212, 175, 55, 0.35)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    Used to automatically generate dynamic payment QR codes with exact order amount
                  </span>
                </div>

                {/* Custom QR Code Image URL */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600 }}>
                    Custom QR Code Image URL / Upload (Optional)
                  </label>
                  <input
                    type="text"
                    value={form.upiQrImage || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, upiQrImage: e.target.value.trim() }))}
                    placeholder="Paste shop QR code image URL (Leave blank for automatic live QR)"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: '#0d0d16',
                      border: '1px solid rgba(212, 175, 55, 0.35)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                    If left blank, an instant scannable UPI QR is generated automatically for customers
                  </span>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB: FIREBASE PHONE OTP LOGIN SUITE */}
        {/* ========================================================================= */}
        {activeTab === 'firebase' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Status & Banner Card */}
            <div style={{
              background: firebaseStatus 
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, #13131e 100%)'
                : 'linear-gradient(135deg, rgba(234, 179, 8, 0.12) 0%, #13131e 100%)',
              border: firebaseStatus ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(234, 179, 8, 0.4)',
              borderRadius: 'var(--radius-md)',
              padding: '1.8rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #0c2340 0%, #001226 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffe500'
                  }}>
                    <Smartphone size={24} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                      Google Firebase Phone Authentication & SMS OTP
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '3px 0 0' }}>
                      Deliver real 6-digit SMS OTP verification to customers on mobile number login
                    </p>
                  </div>
                </div>

                <div style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: firebaseStatus ? 'rgba(34, 197, 94, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                  color: firebaseStatus ? '#4ade80' : '#facc15',
                  border: firebaseStatus ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(234, 179, 8, 0.3)'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: firebaseStatus ? '#22c55e' : '#eab308'
                  }} />
                  {firebaseStatus ? 'REAL SMS OTP ACTIVE' : 'SETUP REQUIRED (DEMO OTP 1234 ACTIVE)'}
                </div>
              </div>

              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                {firebaseStatus 
                  ? 'Google Firebase Phone Authentication is configured and active! When customers log in with their 10-digit mobile number, Firebase will send a genuine 6-digit verification code directly to their phone via SMS.'
                  : 'Firebase is not yet configured. The storefront is currently using Demo Mode (test OTP: 1234). Follow the 5 simple steps below or paste your Firebase Web App snippet to activate real SMS OTP delivery.'}
              </p>
            </div>

            {/* Quick-Paste JSON / Snippet Box */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.6rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <Sparkles size={18} color="var(--gold-400)" />
                <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  Quick Setup: Paste Entire Firebase Config Snippet
                </h4>
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
                Copy the code snippet directly from Firebase Console (Project Settings → Your apps → SDK setup/configuration) and paste it below. Our parser will automatically extract your keys!
              </p>

              <textarea
                rows={5}
                value={firebaseRawSnippet}
                onChange={(e) => setFirebaseRawSnippet(e.target.value)}
                placeholder={`const firebaseConfig = {\n  apiKey: "AIzaSy...",\n  authDomain: "mljewellers.firebaseapp.com",\n  projectId: "mljewellers",\n  storageBucket: "mljewellers.firebasestorage.app",\n  messagingSenderId: "...",\n  appId: "..."\n};`}
                style={{
                  width: '100%',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(212, 175, 55, 0.35)',
                  borderRadius: '8px',
                  color: '#e5e7eb',
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  padding: '12px',
                  resize: 'vertical',
                  marginBottom: '1rem'
                }}
              />

              <button
                type="button"
                onClick={handleParseRawSnippet}
                className="btn btn-gold btn-sm"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 700,
                  padding: '8px 18px'
                }}
              >
                <Sparkles size={15} /> Auto-Extract & Activate Firebase
              </button>
            </div>

            {/* Manual Fields Card */}
            <div style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.6rem'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#fff', margin: '0 0 1.2rem' }}>
                Firebase Project API Credentials
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Web API Key <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. AIzaSyBxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={firebaseConfigState.apiKey || ''}
                    onChange={(e) => setFirebaseConfigState(prev => ({ ...prev, apiKey: e.target.value.trim() }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Project ID <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. mljewellers-auth"
                    value={firebaseConfigState.projectId || ''}
                    onChange={(e) => setFirebaseConfigState(prev => ({ ...prev, projectId: e.target.value.trim() }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Auth Domain
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. mljewellers-auth.firebaseapp.com"
                    value={firebaseConfigState.authDomain || ''}
                    onChange={(e) => setFirebaseConfigState(prev => ({ ...prev, authDomain: e.target.value.trim() }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    App ID
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 1:1234567890:web:abcdef123456"
                    value={firebaseConfigState.appId || ''}
                    onChange={(e) => setFirebaseConfigState(prev => ({ ...prev, appId: e.target.value.trim() }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Storage Bucket
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. mljewellers-auth.firebasestorage.app"
                    value={firebaseConfigState.storageBucket || ''}
                    onChange={(e) => setFirebaseConfigState(prev => ({ ...prev, storageBucket: e.target.value.trim() }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    Messaging Sender ID
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 1234567890"
                    value={firebaseConfigState.messagingSenderId || ''}
                    onChange={(e) => setFirebaseConfigState(prev => ({ ...prev, messagingSenderId: e.target.value.trim() }))}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.88rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1.4rem' }}>
                <button
                  type="button"
                  onClick={handleSaveFirebaseConfig}
                  className="btn btn-gold btn-sm"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 700,
                    padding: '9px 22px'
                  }}
                >
                  <Save size={16} /> Save Firebase OTP Configuration
                </button>
              </div>
            </div>

            {/* Step-by-Step Hindi / English Guide */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 'var(--radius-md)',
              padding: '1.6rem'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#fff', margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📖</span> Firebase Setup Guide (Firebase Console se setup kaise karein)
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '6px', borderLeft: '3px solid var(--gold-400)' }}>
                  <strong style={{ color: '#fff' }}>Step 1: Firebase Project Create Karein</strong><br />
                  Aapke open Firebase Console par bada button <strong>"Get started by setting up a Firebase project"</strong> par click karein. Project ka name daalein (jaise: <code>mljewellers</code>) aur <strong>Continue</strong> karein.
                </div>

                <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '6px', borderLeft: '3px solid var(--gold-400)' }}>
                  <strong style={{ color: '#fff' }}>Step 2: Phone Authentication Enable Karein</strong><br />
                  Project open hone par left side menu me <strong>Build → Authentication</strong> par click karein. <strong>"Get started"</strong> karein, phir <strong>"Sign-in method"</strong> tab me jaakar <strong>"Phone"</strong> provider ko select karke <strong>Enable</strong> toggle on karein aur <strong>Save</strong> karein.
                </div>

                <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '6px', borderLeft: '3px solid var(--gold-400)' }}>
                  <strong style={{ color: '#fff' }}>Step 3: Web App Add Karein</strong><br />
                  Project overview me gear icon <strong>⚙️ Project settings</strong> me jayein. Neeche <strong>"Your apps"</strong> section me Web icon <strong><code>{'</>'}</code></strong> par click karein. App nickname me <code>ML Jewellers Web</code> likhein aur <strong>Register app</strong> par click karein.
                </div>

                <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '6px', borderLeft: '3px solid var(--gold-400)' }}>
                  <strong style={{ color: '#fff' }}>Step 4: Config Code Copy & Paste Karein</strong><br />
                  Wahan screen par aapko <code>const firebaseConfig = &#123; ... &#125;;</code> dikhega. Usko copy karein aur upar <strong>"Quick Setup"</strong> box me paste karke <strong>"Auto-Extract & Activate Firebase"</strong> daba dein!
                </div>

                <div style={{ padding: '10px 14px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '6px', borderLeft: '3px solid var(--gold-400)' }}>
                  <strong style={{ color: '#fff' }}>Step 5: Testing Phone Numbers (Optional)</strong><br />
                  Firebase Authentication → Sign-in method → Phone me <strong>"Phone numbers for testing"</strong> add kar sakte hain (e.g. <code>+91 9829012345</code> with test OTP <code>123456</code>) bina real SMS kharche ke test karne ke liye.
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Global Save Button Bar */}
        <div style={{
          position: 'sticky',
          bottom: '1rem',
          background: '#13131e',
          border: '1px solid var(--border-gold)',
          borderRadius: 'var(--radius-md)',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
          zIndex: 10
        }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Changes apply in real-time across the storefront, header, footer, and generated invoices.
          </div>

          <button
            type="submit"
            className="btn btn-gold"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 800,
              padding: '0.65rem 1.8rem',
              boxShadow: 'var(--shadow-gold)'
            }}
          >
            <Save size={16} /> Save Settings & White-Label
          </button>
        </div>

      </form>

      {/* RESET CONFIRMATION MODAL */}
      {resetConfirmOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: '#13131e',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: 'var(--radius-lg)',
            width: '100%',
            maxWidth: '460px',
            padding: '1.6rem',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', color: '#f87171', marginBottom: '0.8rem' }}>
              <AlertTriangle size={24} />
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>
                Restore Factory Settings?
              </h3>
            </div>

            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1.2rem' }}>
              This will overwrite all custom store configurations, contact details, white-label policies, and restore original factory defaults. Are you certain?
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem' }}>
              <button
                type="button"
                onClick={() => setResetConfirmOpen(false)}
                className="btn btn-glass"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  resetToDefaultData();
                  setResetConfirmOpen(false);
                  setForm(storeSettings);
                }}
                className="btn btn-sm"
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  padding: '0.5rem 1.1rem',
                  borderRadius: 'var(--radius-sm)'
                }}
              >
                Confirm Factory Reset
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
